import { Grid2, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";

import { FooterDetailsButtons } from "@/controls/buttons/FooterDetailsButtons";
import { Guide, GUIDE_INITIAL_VALUES } from "@/pages/catalogs/models/Guide";
import { Stand } from "@/pages/configuration/models/Stand";
import { PartFields } from "@/pages/configuration/pages/parts/PartFields";
import {
	StandGrid,
	useStandsByPart,
} from "@/pages/configuration/pages/parts/StandGrid";
import { setStandsToPart } from "@/pages/configuration/repositories/StandRepository";
import { CommentsField } from "@/shared/components/CommentsField";
import { Section } from "@/shared/models/Section";
import { setSectionsToPart } from "@/shared/repositories/SectionRepository";
import { SectionField } from "@/shared/selectors/SectionField";
import { useSectionsByPart } from "@/shared/selectors/SectionsOfPartSelector";
import { ErrorModal, HelmetDexteel } from "@dexteel/mesf-core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { GuideTypeSelector } from "../../../../../shared/selectors";
import { upsertGuide } from "../../../repositories/GuideRepository";

type Props = {
	guide: Guide | null;
	modal: "create" | "update" | "delete" | "";
	onHide: ({
		shouldUpdate,
		close,
		entityId,
	}: {
		shouldUpdate: boolean;
		close: boolean;
		entityId?: number;
	}) => void;
};

export const GuideDetails = ({ guide, modal, onHide }: Props) => {
	const [error, setError] = useState<string>("");
	const [isSubmitLoading, setIsSubmitLoading] = useState<boolean>(false);

	const [standsSelected, setStandsSelected] = useState<Stand[]>([]);
	const [sectionsSelected, setSectionsSelected] = useState<Section[]>([]);

	const {
		control,
		reset,
		handleSubmit,
		getValues,
		formState: { dirtyFields },
	} = useForm<Guide>({
		defaultValues: GUIDE_INITIAL_VALUES,
		mode: "onChange",
	});

	const { data: sectionsOfPart, isSuccess: sectionsIsSuccess } =
		useSectionsByPart({
			partId: guide?.PartId ?? 0,
			onError: (error) => setError(error.message),
		});
	const sectionsJson = JSON.stringify(
		sectionsSelected.map((section: Section) => ({
			...section,
			PartId: guide?.PartId,
			PartName: guide?.PartName,
		})),
	);
	const oldSectionsJson = JSON.stringify(sectionsOfPart);

	const { data: standsOfPart, isSuccess: standsIsSuccess } = useStandsByPart({
		partId: guide?.PartId!,
		onError: (error) => setError(error.message),
	});
	const standsJson = JSON.stringify(
		standsSelected.map((stand: Stand) => ({
			...stand,
			PartId: guide?.PartId,
			PartName: guide?.PartName,
		})),
	);
	const oldStandsJson = JSON.stringify(standsOfPart);

	const isFormDirty =
		!(Object.keys(dirtyFields).length === 0) ||
		standsJson !== oldStandsJson ||
		sectionsJson !== oldSectionsJson ||
		modal == "create";

	const queryClient = useQueryClient();
	const upsertMutation = useMutation(upsertGuide, {
		meta: { successMessage: `The guide was ${modal}d successfully` },
		onSuccess: async (id: number) => {
			if (id) {
				const resp = await setStandsToPart(id, standsSelected);
				if (!resp.ok) setError(resp.message);
			}
			if (id) {
				const resp = await setSectionsToPart(id, sectionsSelected);
				if (!resp.ok) setError(resp.message);
			}

			await queryClient.invalidateQueries({ queryKey: ["guides"] });
			await queryClient.invalidateQueries({ queryKey: ["sectionsByPart", id] });
			await queryClient.invalidateQueries({ queryKey: ["standsByPart", id] });
			onHide({ shouldUpdate: true, close: false, entityId: id });
		},
		onError: (error: Error) => {
			setError(error.message);
		},
		onSettled: () => setIsSubmitLoading(false),
	});

	const onSubmit: SubmitHandler<Guide> = async (data) => {
		setIsSubmitLoading(true);
		if (modal !== "delete") {
			if (guide?.IsDuplicate) {
				upsertMutation.mutate({ ...data, PartId: null });
			} else {
				upsertMutation.mutate(data);
			}
		}
	};

	useEffect(() => {
		if (sectionsOfPart !== undefined) {
			setSectionsSelected(sectionsOfPart || []);
		}
	}, [sectionsIsSuccess, sectionsOfPart]);
	useEffect(() => {
		if (standsOfPart !== undefined) {
			setStandsSelected(standsOfPart || []);
		}
	}, [standsIsSuccess, standsOfPart]);

	useEffect(() => {
		if (modal !== "") {
			reset(guide || GUIDE_INITIAL_VALUES);
			setIsSubmitLoading(false);
			if (guide?.IsDuplicate) {
				if (standsOfPart && standsIsSuccess) {
					setStandsSelected(standsOfPart);
				}
				if (sectionsOfPart && sectionsIsSuccess) {
					setSectionsSelected(sectionsOfPart);
				}
			} else {
				setStandsSelected(standsOfPart || []);
				setSectionsSelected(sectionsOfPart || []);
			}
		}
	}, [modal, guide]);

	return (
		<>
			{(modal === "create" || modal === "update") && (
				<HelmetDexteel
					title={`${
						guide === null ? "New Guide" : `${guide?.PartName ?? ""}`
					} - Guides`}
				/>
			)}
			{guide === null && modal !== "create" ? (
				<div
					style={{
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						height: "100%",
						width: "100%",
					}}
				>
					<Typography>Select a guide or create a new one</Typography>
				</div>
			) : (
				<form onSubmit={handleSubmit(onSubmit)}>
					<Grid2 container spacing={1} p={1}>
						<PartFields<Guide>
							partTypeCode="G"
							control={control}
							locationSizeMd={6}
							disabled={modal === "delete"}
						/>
						<Grid2 size={{ md: 12, xs: 12 }}>
							<Controller
								name="GuideTypeCode"
								control={control}
								rules={{ required: "Type is required" }}
								render={({ field, fieldState: { error } }) => (
									<GuideTypeSelector
										label="Type"
										value={field.value}
										onChange={field.onChange}
										fieldError={error}
										disabled={modal === "delete"}
										required
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={{ md: 12, xs: 12 }}>
							<SectionField
								label="Sections"
								onSelectionChanged={setSectionsSelected}
								sectionsOfPart={sectionsSelected}
								disabled={modal === "delete"}
								partId={0}
							/>
						</Grid2>
						<StandGrid
							onSelectionChanged={setStandsSelected}
							disabled={modal === "delete"}
							standsOfPart={standsSelected}
						/>
						<Grid2 size={{ md: 12, xs: 12 }}>
							<Controller
								name="Comments"
								control={control}
								render={({ field, fieldState: { error } }) => (
									<CommentsField
										value={field.value}
										onChange={field.onChange}
										fieldError={error}
										getValues={getValues}
										disabled={modal === "delete"}
									/>
								)}
							/>
						</Grid2>
						<FooterDetailsButtons
							isSubmitLoading={isSubmitLoading}
							disabled={!isFormDirty}
							onHide={() => onHide({ shouldUpdate: false, close: true })}
						/>
					</Grid2>
				</form>
			)}
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
